import {
  ApplicationRef,
  ComponentRef,
  EnvironmentInjector,
  Injectable,
  createComponent,
} from '@angular/core';
import {
  ChatInactivityModalComponent,
  ChatModalButton,
  ChatModalButtonClick,
  ChatModalInstance,
} from '../components/chat-modal/chat-modal.component';
import { Subject } from 'rxjs';

export interface ChatModalOptions {
  id?: string;
  showCloseButton?: boolean;
  buttons?: ChatModalButton[];
}

@Injectable({
  providedIn: 'root',
})
export class ChatModalService {
  private modalComponentRef: ComponentRef<ChatInactivityModalComponent> | null =
    null;
  public readonly buttonClick$ = new Subject<ChatModalButtonClick>();
  private readonly modals: ChatModalInstance[] = [];
  private modalSequence = 0;

  constructor(
    private appRef: ApplicationRef,
    private environmentInjector: EnvironmentInjector,
  ) {}

  public exists(modalId: string): boolean {
    return this.modals.some((modal) => modal.id === modalId);
  }

  public showModal(message: string, options?: ChatModalOptions): string {
    const modalId = options?.id ?? this.createModalId();

    this.modals.push({
      id: modalId,
      message,
      showCloseButton: options?.showCloseButton ?? true,
      buttons: options?.buttons ?? [],
    });

    this.ensureHost();
    this.renderModals();

    return modalId;
  }

  public setMessage(message: string, modalId?: string): void {
    this.updateModal(modalId, (modal) => {
      modal.message = message;
    });
  }

  public setButtons(buttons: ChatModalButton[], modalId?: string): void {
    this.updateModal(modalId, (modal) => {
      modal.buttons = buttons;
    });
  }

  public setShowCloseButton(showCloseButton: boolean, modalId?: string): void {
    this.updateModal(modalId, (modal) => {
      modal.showCloseButton = showCloseButton;
    });
  }

  public hideModal(modalId: string): void {
    if (!modalId)
      throw new Error('modalId is required to hide a specific modal.');

    const modalIndex = this.modals.findIndex((modal) => modal.id === modalId);
    if (modalIndex < 0) return;

    this.modals.splice(modalIndex, 1);
    this.renderOrDestroyHost();
  }

  public hideAllModals(): void {
    this.modals.splice(0, this.modals.length);
    if (!this.modalComponentRef) return;

    this.appRef.detachView(this.modalComponentRef.hostView);
    this.modalComponentRef.destroy();
    this.modalComponentRef = null;
  }

  private createModalId(): string {
    this.modalSequence += 1;
    return `chat-modal-${this.modalSequence}`;
  }

  private ensureHost(): void {
    if (this.modalComponentRef) return;

    this.modalComponentRef = createComponent(ChatInactivityModalComponent, {
      environmentInjector: this.environmentInjector,
    });

    this.modalComponentRef.instance.buttonClick.subscribe((event) =>
      this.buttonClick$.next(event),
    );

    this.appRef.attachView(this.modalComponentRef.hostView);
    document.body.appendChild(this.modalComponentRef.location.nativeElement);
  }

  private renderModals(): void {
    if (!this.modalComponentRef) return;

    this.modalComponentRef.setInput('modals', [...this.modals]);
  }

  private renderOrDestroyHost(): void {
    if (this.modals.length === 0) {
      this.hideAllModals();
      return;
    }

    this.renderModals();
  }

  private updateModal(
    modalId: string | undefined,
    update: (modal: ChatModalInstance) => void,
  ): void {
    const targetModalId = modalId ?? this.modals[this.modals.length - 1]?.id;

    if (!targetModalId) return;

    const modalIndex = this.modals.findIndex(
      (modal) => modal.id === targetModalId,
    );

    if (modalIndex < 0) return;

    update(this.modals[modalIndex]);
    this.renderModals();
  }
}
